import { css } from '@emotion/css';
import { Button, BulletPoints } from '@volumental/common';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../../components/modal/modal';
import { uiConfig } from '../../../services/config';
import { isAppleDevice } from '../../../util/utils';

const scrollViewStyle = css`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  margin-bottom: 1rem;
`;
const fontStyle = css`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: auto;
  font-family: ${uiConfig.styles.fontFamily};
  color: ${uiConfig.styles.colors.black};
`;
const h1 = css`
  ${fontStyle}
  font-weight: ${uiConfig.styles.fontWeights.bold};
  font-size: ${uiConfig.styles.fontSizes.medium};
  padding-bottom: 1rem;
  padding-top: 2rem;
`;

const h2 = css`
  ${fontStyle}
  font-weight: ${uiConfig.styles.fontWeights.bold};
  font-size: ${uiConfig.styles.fontSizes.regular};
  padding-bottom: 2rem;
`;

const listStyle = css`
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding-left: 1.2rem;
  font-size: ${uiConfig.styles.fontSizes.regular};
  font-weight: ${uiConfig.styles.fontWeights.regular};
`;

const titleStyle = css`
  ${fontStyle}
  font-weight: ${uiConfig.styles.fontWeights.regular};
  font-size: ${uiConfig.styles.fontSizes.large};
  padding-bottom: 1rem;
`;

interface CameraInstructorProps {
  show: boolean;
  onClose: () => void;
}

export function CameraInstructionModal({
  show,
  onClose,
}: CameraInstructorProps): JSX.Element {
  const { t } = useTranslation();

  const title = isAppleDevice()
    ? t('osCameraInstructor.ios.title')
    : t('osCameraInstructor.android.title');
  const bullets: string[] = isAppleDevice()
    ? (t('osCameraInstructor.ios.bulletPoints', {
        returnObjects: true,
      }) as Array<string>)
    : (t('osCameraInstructor.android.bulletPoints', {
        returnObjects: true,
      }) as Array<string>);
  return (
    <Modal
      close={onClose}
      show={show}
      modalTitle={t('cameraPermissionAlert.title')}
    >
      <div className={scrollViewStyle}>
        <div className={titleStyle}>{t('cameraPermissionAlert.title')}</div>

        <div className={h1}>{t('cameraPermissionAlert.description')}</div>
        <div>
          <div className={h2} key={title}>
            {title}
            <div className={listStyle} key={title}>
              <BulletPoints bulletPoints={bullets} />
            </div>
          </div>
        </div>
      </div>
      <Button onClick={onClose}>
        {t('cameraPermissionAlert.buttonTitle')}
      </Button>
    </Modal>
  );
}
